import { useCallback, useMemo } from "react";

import { Info } from "@magnetic/icons";
import { Select } from "@magnetic/select";
import { SelectOption } from "@magnetic/select/dist/types/types";
import { Tooltip } from "@magnetic/tooltip";

import { HFlex } from "src/components/hFlex";
import RequiredFieldMarker from "src/components/requiredFieldMarker";
import { Geo, geoToJSON } from "src/gen/schema/models/global";

import { GeographicRegion, REGIONS } from "./regionTypes";

import EuFlag from "src/assets/icons/flag-eu.svg?react";
import UsFlag from "src/assets/icons/flag-us.svg?react";
import "./regionFlag.scss";

const allRegionOptions: SelectOption[] = [
  {
    label: REGIONS[GeographicRegion.US].label,
    value: GeographicRegion.US,
    icon: <UsFlag width={16} height={11} />
  },
  {
    label: REGIONS[GeographicRegion.EU].label,
    value: GeographicRegion.EU,
    icon: <EuFlag width={16} height={11} />
  }
];

const UNSPECIFIED_REGION_VALUE = geoToJSON(Geo.GEO_UNSPECIFIED);

const unspecifiedRegionOption: SelectOption = {
  label: "Unspecified",
  value: UNSPECIFIED_REGION_VALUE
};

interface RegionSelectProps {
  readonly value: GeographicRegion | undefined;
  readonly onChange: (value: GeographicRegion | undefined) => void;
  readonly size?: "sm" | "md";
  readonly disabled?: boolean;
  readonly includeUnspecified?: boolean;
  readonly required?: boolean;
  readonly disabledValues?: ReadonlySet<GeographicRegion>;
}

/**
 * Dropdown for selecting a geographic region (NA/US or EU).
 * Used in the Create Organization modal and the Welcome screen.
 */
export const RegionSelect = ({
  value,
  onChange,
  size = "md",
  disabled = false,
  includeUnspecified = false,
  required = true,
  disabledValues
}: RegionSelectProps) => {
  const options = useMemo(() => {
    const regionOptions = disabledValues?.size
      ? allRegionOptions.filter(
          (o) => !disabledValues.has(o.value as GeographicRegion)
        )
      : allRegionOptions;
    return includeUnspecified
      ? [unspecifiedRegionOption, ...regionOptions]
      : regionOptions;
  }, [disabledValues, includeUnspecified]);

  const selectedValue =
    includeUnspecified && value === undefined
      ? UNSPECIFIED_REGION_VALUE
      : value ?? "";

  const handleChange = useCallback(
    (val: string) => {
      if (val === UNSPECIFIED_REGION_VALUE) {
        onChange(undefined);
        return;
      }
      const region = val as GeographicRegion;
      onChange(region || undefined);
    },
    [onChange]
  );

  return (
    <div className="region-select">
      <HFlex gap={4} align="center" className="region-select-label">
        <span>Geographic area</span>
        {required && <RequiredFieldMarker />}
        <Tooltip title="Select the geographic region where your organization data will be stored.">
          <Info size={14} />
        </Tooltip>
      </HFlex>
      <Select
        data-qa="region-select"
        placeholder="Select organization region"
        options={options}
        value={selectedValue}
        onChange={handleChange}
        size={size}
        rootId="region-select"
        disabled={disabled}
      />
    </div>
  );
};
